import React, { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  ReactFlow, Background, Controls, Handle, Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  ArrowLeft, Loader2, Wand2, Sparkles, Send, Lock, Unlock,
  Radio, Activity, MessagesSquare, ChevronDown, ChevronUp, Save, X, Plus, Target,
  User as UserIcon, MapPin, Briefcase, Hash as HashIcon, CheckCircle2,
} from "lucide-react";

const RADIAL = "radial", FORCE = "force", TIMELINE = "timeline";

function Chip({ children, onRemove, testId }) {
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1.5 px-2 py-1 text-[11px] border border-white/15 text-zinc-300 bg-[#050505]">
      {children}
      {onRemove && (
        <button onClick={onRemove} className="hover:text-[#FF3B30]"><X className="w-3 h-3"/></button>
      )}
    </span>
  );
}

function TopicNode({ data }) {
  return (
    <div className="px-3 py-2 border border-white/15 bg-[#09090B] text-[12px] font-medium min-w-[120px] text-center hover:border-[#FF3B30]/60 transition-colors">
      <Handle type="target" position={Position.Left}/>
      {data.label}
      {data.count != null && <div className="font-mono text-[9px] text-zinc-500 mt-0.5">{data.count} activities</div>}
      <Handle type="source" position={Position.Right}/>
    </div>
  );
}
function SubredditNode({ data }) {
  return (
    <div className="px-2 py-1 border border-white/10 bg-[#050505] text-[11px] text-[#FF3B30] font-mono min-w-[80px] text-center">
      <Handle type="target" position={Position.Left}/>
      {data.label}
      <Handle type="source" position={Position.Right}/>
    </div>
  );
}
function CenterNode({ data }) {
  return (
    <div className="px-4 py-3 border-2 border-[#FF3B30] bg-[#141416] text-[13px] font-display font-black rounded-full">
      <Handle type="source" position={Position.Right}/>
      {data.label}
    </div>
  );
}
const nodeTypes = { topic: TopicNode, subreddit: SubredditNode, center: CenterNode };

function radialLayout(nodes, edges) {
  // 3-ring radial: center -> topics (ring 1) -> subreddits (ring 2)
  const center = nodes.find(n => n.type === "center") || nodes[0];
  const topics = nodes.filter(n => n.type === "topic");
  const laid = [{ ...center, position: { x: 0, y: 0 } }];
  const R1 = 220, R2 = 380;
  topics.forEach((t, i) => {
    const angle = (i / Math.max(1, topics.length)) * Math.PI * 2;
    laid.push({ ...t, position: { x: Math.cos(angle) * R1, y: Math.sin(angle) * R1 } });
  });
  const subredditByTopic = {};
  edges.forEach(e => {
    const src = laid.find(n => n.id === e.source);
    if (src?.type !== "topic") return;
    subredditByTopic[e.source] = subredditByTopic[e.source] || [];
    subredditByTopic[e.source].push(e.target);
  });
  topics.forEach((t) => {
    const src = laid.find(n => n.id === t.id);
    const kids = subredditByTopic[t.id] || [];
    kids.forEach((sid, j) => {
      const child = nodes.find(n => n.id === sid);
      if (!child) return;
      const spread = 0.35;
      const parentAngle = Math.atan2(src.position.y, src.position.x);
      const angle = parentAngle + (j - (kids.length - 1) / 2) * spread;
      laid.push({ ...child, position: { x: Math.cos(angle) * R2, y: Math.sin(angle) * R2 } });
    });
  });
  return laid;
}
function forceLayout(nodes) {
  // Simple deterministic circle-pack pseudo-force layout
  return nodes.map((n, i) => {
    const angle = (i / Math.max(1, nodes.length)) * Math.PI * 2;
    const R = 220 + (n.size || 1) * 12;
    return { ...n, type: n.kind === "topic" ? "topic" : "subreddit",
             position: { x: Math.cos(angle) * R, y: Math.sin(angle) * R } };
  });
}

function MindMap({ agentId, agentName }) {
  const [style, setStyle] = useState(RADIAL);
  const q = useQuery({
    queryKey: ["mindmap", agentId, style],
    queryFn: () => api.get(`/agents/${agentId}/mindmap?style=${style}`).then(r => r.data),
  });

  const { rfNodes, rfEdges, timeline } = useMemo(() => {
    if (!q.data) return { rfNodes: [], rfEdges: [], timeline: null };
    if (q.data.style === "timeline") {
      return { rfNodes: [], rfEdges: [], timeline: q.data };
    }
    const rawNodes = (q.data.nodes || []).map(n => {
      if (q.data.style === "radial") return { id: n.id, type: n.type, data: { label: n.label, count: n.count } };
      return { id: n.id, kind: n.kind, size: n.size, data: { label: n.label } };
    });
    let laid = [];
    if (q.data.style === "radial") laid = radialLayout(rawNodes, q.data.edges || []);
    if (q.data.style === "force") laid = forceLayout(rawNodes);
    // Inject center for radial if not present
    if (q.data.style === "radial" && !laid.find(n => n.id === "agent")) {
      laid.unshift({ id: "agent", type: "center", data: { label: agentName }, position: { x: 0, y: 0 } });
    } else if (q.data.style === "radial") {
      // ensure center label reflects agent name
      const c = laid.find(n => n.id === "agent");
      if (c) c.data.label = agentName;
    }
    const rfEdges = (q.data.edges || []).map((e, i) => ({
      id: `e${i}`, source: e.source, target: e.target,
      style: { stroke: "rgba(255,255,255,0.16)" },
    }));
    return { rfNodes: laid, rfEdges, timeline: null };
  }, [q.data, agentName]);

  return (
    <div className="border border-white/10">
      <div className="flex items-center gap-1 border-b border-white/10 px-3 py-2">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mr-3">// mind map</div>
        {[[RADIAL, "Radial"], [FORCE, "Force"], [TIMELINE, "Timeline"]].map(([k, label]) => (
          <button key={k}
            onClick={() => setStyle(k)}
            data-testid={`mindmap-tab-${k}`}
            className={`px-3 py-1 text-[11px] font-mono uppercase tracking-widest border ${
              style === k ? "bg-white text-black border-white" : "border-white/10 text-zinc-400 hover:text-white"
            }`}
          >{label}</button>
        ))}
        <div className="ml-auto text-[11px] text-zinc-500 font-mono">
          {q.data?.summary?.slice(0, 90)}
        </div>
      </div>
      {q.isLoading ? (
        <div className="p-16 text-center text-zinc-500 text-sm flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin"/> Building map…
        </div>
      ) : timeline ? (
        <div className="p-4 overflow-x-auto" data-testid="mindmap-timeline">
          {timeline.grid.length === 0 && <div className="text-zinc-500 text-sm text-center py-10">No topics extracted yet.</div>}
          {timeline.grid.length > 0 && (
            <table className="w-full min-w-[600px]">
              <thead>
                <tr>
                  <th className="text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500 pb-2">Topic</th>
                  {Array.from({length: timeline.weeks}, (_, i) => (
                    <th key={i} className="font-mono text-[10px] text-zinc-500 pb-2">
                      W{timeline.weeks - i}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {timeline.grid.map((row, ri) => {
                  const max = Math.max(1, ...row.buckets);
                  return (
                    <tr key={ri}>
                      <td className="pr-4 py-1 text-[12px] text-zinc-200 truncate max-w-[220px]">{row.topic}</td>
                      {row.buckets.map((v, i) => {
                        const alpha = v ? Math.max(0.15, v / max) : 0;
                        return (
                          <td key={i} className="p-1">
                            <div title={`${v} activities`} style={{ background: v ? `rgba(255,59,48,${alpha})` : "rgba(255,255,255,0.03)" }}
                                 className="h-8 w-full border border-white/5 flex items-center justify-center text-[10px] font-mono text-white/80">
                              {v || ""}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="h-[440px] bg-[#050505] bg-dots overflow-hidden" data-testid={`mindmap-${style}`}>
          {rfNodes.length === 0 ? (
            <div className="h-full flex items-center justify-center text-zinc-500 text-sm">
              No activity yet — completed tasks and drift will populate this map.
            </div>
          ) : (
            <ReactFlow
              nodes={rfNodes}
              edges={rfEdges}
              nodeTypes={nodeTypes}
              fitView
              proOptions={{ hideAttribution: true }}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              zoomOnScroll={false}
              panOnDrag
            >
              <Background gap={20} size={1} color="rgba(255,255,255,0.05)"/>
              <Controls position="bottom-right" showInteractive={false}/>
            </ReactFlow>
          )}
        </div>
      )}
    </div>
  );
}

function PersonaOptionsModal({ agentId, hint, setHint, onApply, onClose }) {
  const [options, setOptions] = useState([]);
  const [count, setCount] = useState(4);
  const gen = useMutation({
    mutationFn: () => api.post(`/agents/${agentId}/generate-personas`, { count, hint: hint || null }, { timeout: 85000 }),
    onSuccess: (r) => {
      setOptions(r.data.personas || []);
      if ((r.data.personas || []).length === 0) toast.error("AI returned nothing — try again");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Generation failed"),
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6" onClick={onClose} data-testid="persona-options-modal">
      <div className="bg-[#0a0a0b] border border-white/15 w-full max-w-5xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">// jarvis · persona lab</div>
            <div className="font-display font-black text-2xl tracking-tight">Generate identities.</div>
          </div>
          <button onClick={onClose} className="p-1.5 border border-white/10 hover:border-white/30" data-testid="close-persona-modal">
            <X className="w-4 h-4"/>
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-12 gap-3 items-end">
            <div className="col-span-12 sm:col-span-9">
              <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Hint (optional)</label>
              <input
                type="text"
                value={hint}
                onChange={(e) => setHint(e.target.value)}
                placeholder="e.g. tier-2 city, gaming vibe / mumbai finance grad / kerala home-cook"
                data-testid="persona-hint-input"
                className="w-full bg-[#050505] border border-white/10 focus:border-white/30 px-3 py-2 text-[13px] text-zinc-200"
              />
            </div>
            <div className="col-span-4 sm:col-span-1">
              <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Count</label>
              <input
                type="number" min={1} max={6}
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value, 10) || 4)}
                data-testid="persona-count-input"
                className="w-full bg-[#050505] border border-white/10 px-3 py-2 text-[13px] text-zinc-200 font-mono text-center"
              />
            </div>
            <div className="col-span-8 sm:col-span-2">
              <button
                onClick={() => gen.mutate()}
                disabled={gen.isPending}
                data-testid="generate-personas-btn"
                className="w-full bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium py-2 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {gen.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
                GENERATE
              </button>
            </div>
          </div>

          {gen.isPending && (
            <div className="text-center py-16 border border-dashed border-white/10 text-zinc-500 text-sm">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3"/>
              Jarvis is imagining {count} humans — this takes 5-20s.
            </div>
          )}

          {!gen.isPending && options.length === 0 && (
            <div className="text-center py-12 border border-dashed border-white/10 text-zinc-500 text-sm" data-testid="persona-options-empty">
              Click Generate to get {count} distinct persona candidates. Pick one to prefill the editor.
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            {options.map((p, i) => (
              <div key={i} className="border border-white/10 hover:border-[#FF3B30]/60 p-4 flex flex-col" data-testid={`persona-option-${i}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-display font-black text-lg tracking-tight text-white flex items-center gap-2">
                      <UserIcon className="w-4 h-4 text-[#FF3B30]"/>
                      {p.name}, {p.age}
                    </div>
                    <div className="font-mono text-[11px] text-zinc-500 flex items-center gap-3 mt-1">
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/>{p.city}</span>
                      <span className="flex items-center gap-1"><Briefcase className="w-3 h-3"/>{p.profession}</span>
                    </div>
                  </div>
                  {p.handle && (
                    <div className="font-mono text-[10px] text-amber-400 border border-amber-500/30 px-2 py-0.5">
                      u/{p.handle}
                    </div>
                  )}
                </div>
                <div className="text-[12px] text-zinc-300 leading-relaxed mb-3">{p.backstory}</div>
                {p.tone && (
                  <div className="text-[11px] font-mono text-zinc-500 mb-3 border-l-2 border-white/10 pl-2">
                    tone → {p.tone}
                  </div>
                )}
                <div className="mb-2">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-1">interests</div>
                  <div className="flex flex-wrap gap-1">
                    {(p.interests || []).slice(0, 8).map((k, j) => (
                      <span key={`int-${k}-${j}`} className="text-[10px] px-1.5 py-0.5 border border-white/10 text-zinc-300">{k}</span>
                    ))}
                  </div>
                </div>
                <div className="mb-3">
                  <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-500 mb-1">subs</div>
                  <div className="flex flex-wrap gap-1">
                    {(p.subreddits || []).slice(0, 10).map((s, j) => (
                      <span key={`sub-${s}-${j}`} className="text-[10px] px-1.5 py-0.5 border border-white/10 text-zinc-400 font-mono">{s}</span>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => onApply(p)}
                  data-testid={`apply-persona-${i}`}
                  className="mt-auto bg-white/[0.06] hover:bg-[#FF3B30] hover:text-white text-zinc-100 py-2 text-[11px] font-mono uppercase tracking-widest flex items-center justify-center gap-2 border border-white/10"
                >
                  <CheckCircle2 className="w-3.5 h-3.5"/> USE THIS PERSONA
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PersonaWeb({ agentName, interests, subs, redditUname }) {
  const { nodes, edges } = React.useMemo(() => {
    const ns = [];
    const es = [];
    const centerId = "persona-center";
    ns.push({
      id: centerId, type: "center",
      data: { label: redditUname ? `@${redditUname}` : agentName },
      position: { x: 0, y: 0 },
    });
    const uniqueInterests = [...new Set(interests || [])];
    const uniqueSubs = [...new Set(subs || [])];
    const ringR1 = 160, ringR2 = 300;
    uniqueInterests.forEach((k, i) => {
      const angle = (i / Math.max(1, uniqueInterests.length)) * Math.PI * 2;
      const id = `int-${i}`;
      ns.push({ id, type: "topic",
        data: { label: k, count: null },
        position: { x: Math.cos(angle) * ringR1, y: Math.sin(angle) * ringR1 } });
      es.push({ id: `e-c-${id}`, source: centerId, target: id,
                style: { stroke: "rgba(255,59,48,0.35)" } });
    });
    uniqueSubs.forEach((s, i) => {
      const angle = (i / Math.max(1, uniqueSubs.length)) * Math.PI * 2 + Math.PI / uniqueSubs.length;
      const id = `sub-${i}`;
      ns.push({ id, type: "subreddit",
        data: { label: s },
        position: { x: Math.cos(angle) * ringR2, y: Math.sin(angle) * ringR2 } });
      // Attach to the closest interest, so the graph looks connected. If no interests, hang off center.
      if (uniqueInterests.length === 0) {
        es.push({ id: `e-c-${id}`, source: centerId, target: id,
                  style: { stroke: "rgba(255,255,255,0.14)" } });
      } else {
        const nearest = `int-${i % uniqueInterests.length}`;
        es.push({ id: `e-${nearest}-${id}`, source: nearest, target: id,
                  style: { stroke: "rgba(255,255,255,0.14)" } });
      }
    });
    return { nodes: ns, edges: es };
  }, [agentName, interests, subs, redditUname]);

  return (
    <div className="mt-4 border border-white/10 bg-[#050505] h-[280px] overflow-hidden" data-testid="persona-web">
      {nodes.length <= 1 ? (
        <div className="h-full flex items-center justify-center text-center px-6">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">// persona web</div>
            <div className="text-[12px] text-zinc-500">Add interests + subreddits above to see how this operator's identity connects.</div>
          </div>
        </div>
      ) : (
        <ReactFlow
          nodes={nodes} edges={edges} nodeTypes={nodeTypes}
          fitView proOptions={{ hideAttribution: true }}
          nodesDraggable={false} nodesConnectable={false}
          elementsSelectable={false} zoomOnScroll={false} panOnDrag
        >
          <Background gap={16} size={1} color="rgba(255,255,255,0.05)"/>
        </ReactFlow>
      )}
    </div>
  );
}

function ProfileCard({ agent, isAdmin, onSaved }) {
  const qc = useQueryClient();
  const skipStats = useQuery({
    queryKey: ["drift-skip-rate", agent.id],
    queryFn: () => api.get(`/agents/${agent.id}/drift-skip-rate`).then(r => r.data),
    enabled: isAdmin,
    staleTime: 30_000,
  });
  const [form, setForm] = useState({
    age: agent.age ?? "",
    gender: agent.gender || "",
    city: agent.city || "",
    reddit_username: agent.reddit_username || "",
    reddit_account_type: agent.reddit_account_type || "",
    reddit_account_created_at: agent.reddit_account_created_at || "",
    availability_start_hour: agent.availability_start_hour ?? "",
    availability_end_hour: agent.availability_end_hour ?? "",
    availability_timezone: agent.availability_timezone || "Asia/Kolkata",
  });

  const REQUIRED = ["age", "gender", "city", "reddit_username",
                    "reddit_account_type", "reddit_account_created_at",
                    "availability_start_hour", "availability_end_hour"];
  const missing = REQUIRED.filter(k => {
    const v = form[k];
    return v === "" || v === null || v === undefined;
  });
  const complete = missing.length === 0;

  // Warming state
  const warmingStarted = agent.warming_started_at || agent.profile_completed_at;
  const warmedManually = !!agent.warmed_manually;
  let warmingActive = false;
  let warmingDaysLeft = 0;
  if (form.reddit_account_type === "new" && !warmedManually) {
    const started = warmingStarted ? new Date(warmingStarted) : null;
    if (!started) {
      warmingActive = true;
      warmingDaysLeft = 7;
    } else {
      const elapsed = (Date.now() - started.getTime()) / (1000 * 60 * 60 * 24);
      if (elapsed < 7) {
        warmingActive = true;
        warmingDaysLeft = Math.ceil(7 - elapsed);
      }
    }
  }

  const save = useMutation({
    mutationFn: (body) => api.patch(`/agents/${agent.id}/profile`, body),
    onSuccess: () => {
      toast.success("Profile saved");
      qc.invalidateQueries({ queryKey: ["agent", agent.id] });
      onSaved?.();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });

  const warmUnlock = useMutation({
    mutationFn: (unlocked) => api.post(`/agents/${agent.id}/warm-unlock`, { unlocked }),
    onSuccess: () => { toast.success("Warming override updated"); qc.invalidateQueries({ queryKey: ["agent", agent.id] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const submit = () => {
    const body = {};
    if (form.age !== "") body.age = parseInt(form.age, 10);
    if (form.gender) body.gender = form.gender;
    if (form.city) body.city = form.city;
    if (form.reddit_username) body.reddit_username = form.reddit_username;
    if (form.reddit_account_type) body.reddit_account_type = form.reddit_account_type;
    if (form.reddit_account_created_at) body.reddit_account_created_at = form.reddit_account_created_at;
    if (form.availability_start_hour !== "") body.availability_start_hour = parseInt(form.availability_start_hour, 10);
    if (form.availability_end_hour !== "") body.availability_end_hour = parseInt(form.availability_end_hour, 10);
    if (form.availability_timezone) body.availability_timezone = form.availability_timezone;
    // Client-side check: min 6h availability
    if (body.availability_start_hour !== undefined && body.availability_end_hour !== undefined) {
      const span = ((body.availability_end_hour - body.availability_start_hour) % 24 + 24) % 24;
      if (span === 0) { toast.error("Availability start & end can't be equal"); return; }
      if (span < 6) { toast.error("Availability window must be ≥ 6 hours"); return; }
    }
    save.mutate(body);
  };

  return (
    <div className="border border-white/10 p-5" data-testid="profile-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">// mandatory profile</div>
          <h3 className="font-display font-black text-xl tracking-tight">Operator basics.</h3>
        </div>
        {complete ? (
          <span className="text-[10px] font-mono uppercase tracking-widest border border-emerald-500/40 text-emerald-400 px-2 py-1" data-testid="profile-status-complete">
            complete
          </span>
        ) : (
          <span className="text-[10px] font-mono uppercase tracking-widest border border-amber-500/40 text-amber-400 px-2 py-1" data-testid="profile-status-incomplete">
            {missing.length} missing
          </span>
        )}
      </div>

      {!complete && (
        <div className="mb-4 border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-400 leading-relaxed">
          Complete this profile to receive campaign tasks. Fields still needed:
          <div className="mt-1 font-mono text-amber-300">{missing.join(" · ")}</div>
        </div>
      )}

      {warmingActive && (
        <div className="mb-4 border border-[#007AFF]/40 bg-[#007AFF]/5 px-3 py-2 text-[11px] text-[#007AFF] leading-relaxed" data-testid="warming-notice">
          🔥 Warming period active — new Reddit accounts are drift-tasks-only for the first 7 days.
          <div className="mt-0.5 text-zinc-300">Days remaining: <b>{warmingDaysLeft}</b></div>
          {isAdmin && (
            <button
              onClick={() => warmUnlock.mutate(true)}
              disabled={warmUnlock.isPending}
              data-testid="warm-unlock-btn"
              className="mt-2 text-[10px] font-mono uppercase tracking-widest border border-[#007AFF] text-[#007AFF] hover:bg-[#007AFF] hover:text-white px-2 py-1 disabled:opacity-50"
            >
              admin override → unlock for campaigns
            </button>
          )}
        </div>
      )}
      {warmedManually && (
        <div className="mb-4 border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-[11px] text-emerald-400 leading-relaxed" data-testid="warm-unlocked-notice">
          ✅ Warming override active — this agent can join campaigns immediately.
          {isAdmin && (
            <button
              onClick={() => warmUnlock.mutate(false)}
              className="ml-2 underline hover:text-white"
              data-testid="warm-relock-btn"
            >revert</button>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Age *</label>
          <input type="number" min={13} max={99} value={form.age} onChange={(e)=>setForm({...form, age:e.target.value})}
            data-testid="profile-age-input"
            className="w-full bg-[#050505] border border-white/10 px-2 py-1.5 text-[12px] font-mono"/>
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Gender *</label>
          <select value={form.gender} onChange={(e)=>setForm({...form, gender:e.target.value})}
            data-testid="profile-gender-input"
            className="w-full bg-[#050505] border border-white/10 px-2 py-1.5 text-[12px]">
            <option value="">select…</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="prefer_not_to_say">Prefer not to say</option>
          </select>
        </div>
      </div>
      <div className="mb-3">
        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">City / Location *</label>
        <input value={form.city} onChange={(e)=>setForm({...form, city:e.target.value})}
          placeholder="e.g. Bengaluru, Karnataka"
          data-testid="profile-city-input"
          className="w-full bg-[#050505] border border-white/10 px-2 py-1.5 text-[12px]"/>
      </div>

      <div className="border-t border-white/5 pt-3 mb-3">
        <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-600 mb-2">Reddit account</div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Username *</label>
            <input value={form.reddit_username} onChange={(e)=>setForm({...form, reddit_username:e.target.value})}
              placeholder="u/handle"
              data-testid="profile-reddit-uname-input"
              className="w-full bg-[#050505] border border-white/10 px-2 py-1.5 text-[12px] font-mono"/>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Account type *</label>
            <select value={form.reddit_account_type} onChange={(e)=>setForm({...form, reddit_account_type:e.target.value})}
              data-testid="profile-acct-type-input"
              className="w-full bg-[#050505] border border-white/10 px-2 py-1.5 text-[12px]">
              <option value="">select…</option>
              <option value="new">New (locks 7d)</option>
              <option value="old">Old / warmed</option>
            </select>
          </div>
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Account created on *</label>
          <input type="date" value={form.reddit_account_created_at?.slice(0,10)} onChange={(e)=>setForm({...form, reddit_account_created_at:e.target.value})}
            data-testid="profile-acct-created-input"
            className="w-full bg-[#050505] border border-white/10 px-2 py-1.5 text-[12px] font-mono"/>
        </div>
      </div>

      <div className="border-t border-white/5 pt-3 mb-4">
        <div className="text-[9px] font-mono uppercase tracking-widest text-zinc-600 mb-2">Availability window (min 6 hours · Telegram silent outside window)</div>
        <div className="grid grid-cols-2 gap-3 mb-2">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Start hour *</label>
            <select value={form.availability_start_hour} onChange={(e)=>setForm({...form, availability_start_hour:e.target.value})}
              data-testid="profile-avail-start-input"
              className="w-full bg-[#050505] border border-white/10 px-2 py-1.5 text-[12px] font-mono">
              <option value="">--</option>
              {Array.from({length:24}, (_,i) => {
                const label = `${String(i).padStart(2, "0")}:00`;
                return <option key={i} value={i}>{label}</option>;
              })}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">End hour *</label>
            <select value={form.availability_end_hour} onChange={(e)=>setForm({...form, availability_end_hour:e.target.value})}
              data-testid="profile-avail-end-input"
              className="w-full bg-[#050505] border border-white/10 px-2 py-1.5 text-[12px] font-mono">
              <option value="">--</option>
              {Array.from({length:24}, (_,i) => {
                const label = `${String(i).padStart(2, "0")}:00`;
                return <option key={i} value={i}>{label}</option>;
              })}
            </select>
          </div>
        </div>
        <div className="text-[10px] font-mono text-zinc-500">
          Timezone: <span className="text-zinc-300">{form.availability_timezone}</span>
        </div>
      </div>

      {isAdmin && skipStats.data && skipStats.data.drift_ever_assigned > 0 && (
        <div className={`mb-3 border p-3 text-[11px] leading-relaxed ${
          skipStats.data.red_flag
            ? "border-[#FF3B30]/60 bg-[#FF3B30]/5 text-[#FF3B30]"
            : "border-white/10 bg-[var(--bg-2)] text-zinc-400"
        }`} data-testid="drift-skip-rate-badge">
          <div className="flex items-center justify-between">
            <span className="font-mono uppercase tracking-widest text-[10px]">
              drift skip rate
            </span>
            <span className="font-mono font-bold text-[13px]">
              {skipStats.data.skip_rate_percent}%
            </span>
          </div>
          <div className="mt-1 text-[10px]">
            {skipStats.data.drift_deletes} skipped · {skipStats.data.drift_outstanding} outstanding · {skipStats.data.drift_ever_assigned} ever assigned
            {skipStats.data.red_flag && " · 🚩 needs coaching"}
          </div>
        </div>
      )}

      <button
        onClick={submit}
        disabled={save.isPending}
        data-testid="save-profile-btn"
        className="w-full bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {save.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
        SAVE PROFILE
      </button>
    </div>
  );
}

function PersonaEditor({ agent, isAdmin, onSaved }) {
  const [interests, setInterests] = useState(agent.interests || []);
  const [subs, setSubs] = useState(agent.persona_subreddits || []);
  const [redditUname, setRedditUname] = useState(agent.reddit_username || "");
  const [notes, setNotes] = useState(agent.persona_notes || "");
  const [freq, setFreq] = useState(agent.drift_frequency_per_week || 5);
  const [lock, setLock] = useState(agent.persona_locked_by || "");
  const [newIn, setNewIn] = useState(""); const [newSub, setNewSub] = useState("");
  const [openLab, setOpenLab] = useState(false);
  const [personaHint, setPersonaHint] = useState("");

  const applyPersonaOption = (p) => {
    setInterests([...new Set([...(interests), ...(p.interests || [])])]);
    setSubs([...new Set([...(subs), ...(p.subreddits || [])])]);
    if (!redditUname && p.handle) setRedditUname(p.handle);
    // Merge backstory into notes (prepend)
    const bio = [
      p.name && `${p.name}, ${p.age || "??"} · ${p.city || ""} · ${p.profession || ""}`.trim(),
      p.backstory,
      p.tone && `Tone: ${p.tone}`,
      p.persona_notes,
    ].filter(Boolean).join("\n\n");
    setNotes(notes ? `${notes}\n\n---\n\n${bio}` : bio);
    setOpenLab(false);
    toast.success(`Loaded "${p.name}". Review and hit save.`);
  };

  const save = useMutation({
    mutationFn: (body) => api.patch(`/agents/${agent.id}/persona`, body),
    onSuccess: () => { toast.success("Persona saved"); onSaved?.(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  const recommend = useMutation({
    mutationFn: () => api.post(`/agents/${agent.id}/recommend-persona`),
    onSuccess: (r) => {
      const rec = r.data.recommendation || {};
      setInterests([...new Set([...(interests), ...(rec.recommended_interests || [])])]);
      setSubs([...new Set([...(subs), ...(rec.recommended_subreddits || [])])]);
      toast.success(`AI added ${rec.recommended_interests?.length || 0} interests · ${rec.recommended_subreddits?.length || 0} subs. Review & save.`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Recommendation failed"),
  });

  const locked = lock === "admin" && !isAdmin;

  return (
    <div className="border border-white/10 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">// persona</div>
          <h3 className="font-display font-black text-xl tracking-tight">Cover identity.</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => recommend.mutate()}
            disabled={recommend.isPending}
            data-testid="recommend-persona-btn"
            className="border border-white/15 hover:border-white/40 px-3 py-2 text-[12px] flex items-center gap-2 disabled:opacity-50"
            title="Analyze past activity + Reddit profile to suggest additions"
          >
            {recommend.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Wand2 className="w-4 h-4"/>}
            FROM ACTIVITY
          </button>
          <button
            onClick={() => setOpenLab(true)}
            data-testid="open-persona-lab-btn"
            className="bg-[#FF3B30] hover:bg-[#FF5A50] text-white px-3 py-2 text-[12px] flex items-center gap-2 font-medium"
            title="Ask Jarvis to invent full persona candidates from scratch"
          >
            <Sparkles className="w-4 h-4"/>
            AI PERSONA LAB
          </button>
        </div>
      </div>

      {openLab && (
        <PersonaOptionsModal
          agentId={agent.id}
          hint={personaHint}
          setHint={setPersonaHint}
          onApply={applyPersonaOption}
          onClose={() => setOpenLab(false)}
        />
      )}

      <div className="text-[11px] text-zinc-500 leading-relaxed mb-4 pb-3 border-b border-white/5">
        Build this agent's cover identity: list their <b className="text-zinc-300">interests</b> (topics they'd naturally engage with)
        and <b className="text-zinc-300">home subreddits</b> (where they hang out). The web below visualises how these tie together —
        Jarvis uses this map to keep their organic posts on-persona.
      </div>

      <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Interests</label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {interests.map((k, i) => (
          <Chip key={`int-${i}-${k}`} testId={`interest-${k}`} onRemove={locked ? null : () => setInterests(interests.filter(x => x !== k))}>{k}</Chip>
        ))}
        {interests.length === 0 && <span className="text-[11px] font-mono text-zinc-600">— none —</span>}
      </div>
      {!locked && (
        <div className="flex gap-2 mb-4">
          <input placeholder="add interest" value={newIn} onChange={(e)=>setNewIn(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (newIn.trim()) { setInterests([...interests, newIn.trim()]); setNewIn(""); } } }}
                 data-testid="new-interest-input"
                 className="flex-1 bg-[#050505] border border-white/10 px-2 py-1.5 text-[12px]"/>
          <button onClick={() => { if (newIn.trim()) { setInterests([...interests, newIn.trim()]); setNewIn(""); } }} className="border border-white/15 px-2 text-[11px]"><Plus className="w-3 h-3"/></button>
        </div>
      )}

      <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Home subreddits</label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {subs.map((s, i) => (
          <Chip key={`sub-${i}-${s}`} testId={`sub-${s}`} onRemove={locked ? null : () => setSubs(subs.filter(x => x !== s))}>{s}</Chip>
        ))}
        {subs.length === 0 && <span className="text-[11px] font-mono text-zinc-600">— none —</span>}
      </div>
      {!locked && (
        <div className="flex gap-2 mb-4">
          <input placeholder="r/xxx" value={newSub} onChange={(e)=>setNewSub(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (newSub.trim()) { setSubs([...subs, newSub.trim()]); setNewSub(""); } } }}
                 data-testid="new-persona-sub-input"
                 className="flex-1 bg-[#050505] border border-white/10 px-2 py-1.5 text-[12px] font-mono"/>
          <button onClick={() => { if (newSub.trim()) { setSubs([...subs, newSub.trim()]); setNewSub(""); } }} className="border border-white/15 px-2 text-[11px]"><Plus className="w-3 h-3"/></button>
        </div>
      )}

      <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Reddit username (optional)</label>
      <input placeholder="e.g. uday_reddit" value={redditUname} onChange={(e)=>setRedditUname(e.target.value)}
             data-testid="reddit-uname-input" disabled={locked}
             className="w-full bg-[#050505] border border-white/10 px-2 py-1.5 mb-4 text-[12px] font-mono disabled:opacity-50"/>

      <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Persona notes</label>
      <textarea rows={3} placeholder="tone, backstory, taboos to avoid…" value={notes} onChange={(e)=>setNotes(e.target.value)}
                data-testid="persona-notes-input" disabled={locked}
                className="w-full bg-[#050505] border border-white/10 px-2 py-1.5 mb-4 text-[12px] disabled:opacity-50"/>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Drift / week</label>
          <input type="number" min={0} max={30} value={freq} onChange={(e)=>setFreq(parseInt(e.target.value||"0",10))}
                 data-testid="drift-freq-input" disabled={locked}
                 className="w-full bg-[#050505] border border-white/10 px-2 py-1.5 text-[12px] font-mono disabled:opacity-50"/>
        </div>
        {isAdmin && (
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Lock</label>
            <select value={lock} onChange={(e)=>setLock(e.target.value)}
                    data-testid="persona-lock-select"
                    className="w-full bg-[#050505] border border-white/10 px-2 py-1.5 text-[12px]">
              <option value="">unlocked</option>
              <option value="admin">admin-locked</option>
              <option value="ai">ai-suggested</option>
            </select>
          </div>
        )}
      </div>

      <button
        onClick={() => save.mutate({
          interests, persona_subreddits: subs, reddit_username: redditUname || null,
          persona_notes: notes, drift_frequency_per_week: freq,
          persona_locked_by: isAdmin ? (lock || null) : undefined,
        })}
        disabled={save.isPending || locked}
        data-testid="save-persona-btn"
        className="w-full bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {save.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>}
        {locked ? "LOCKED BY ADMIN" : "SAVE PERSONA"}
      </button>

      <PersonaWeb agentName={agent.name} interests={interests} subs={subs} redditUname={redditUname}/>
    </div>
  );
}

function CampaignInvolvement({ agentId }) {
  const q = useQuery({
    queryKey: ["agent-campaigns", agentId],
    queryFn: () => api.get(`/agents/${agentId}/campaigns`).then(r => r.data),
    refetchInterval: 15000,
  });
  return (
    <div className="border border-white/10">
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
        <Target className="w-4 h-4 text-[#FF3B30]"/>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">// campaign involvement</div>
        <div className="ml-auto text-[11px] font-mono text-zinc-500">{q.data?.length || 0} campaign(s)</div>
      </div>
      {q.isLoading && <div className="p-6 text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div>}
      {q.data?.length === 0 && (
        <div className="p-8 text-center text-zinc-500 text-sm">This agent isn't assigned to any campaign yet.</div>
      )}
      <div className="max-h-[380px] overflow-y-auto">
        {q.data?.map((c) => {
          const mp = c.my_progress || {};
          const pct = mp.pct || 0;
          return (
            <div key={c.id} data-testid={`involvement-${c.id}`} className="px-4 py-3 border-b border-white/5 hover:bg-[#0e0e10]">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-zinc-100 truncate">{c.objective}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-[9px] font-mono uppercase tracking-widest border border-white/10 px-1.5 py-0.5 text-zinc-400">
                      {c.admin_approval_status || "?"}
                    </span>
                    {c.product?.name && <span className="text-[9px] font-mono uppercase tracking-widest border border-amber-500/40 text-amber-400 px-1.5 py-0.5">{c.product.name}</span>}
                    {c.thread && <span className="text-[10px] font-mono text-[#FF3B30]">{c.thread.subreddit}</span>}
                  </div>
                </div>
                <a href={`/admin/threads/${c.thread?.id}`}
                   className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white shrink-0">
                  open →
                </a>
              </div>
              <div className="mt-2 h-1.5 bg-[#1a1a1c] relative overflow-hidden">
                <div className="h-full bg-emerald-400 transition-all" style={{ width: `${pct}%` }}/>
              </div>
              <div className="flex items-center justify-between mt-1 font-mono text-[10px] text-zinc-500">
                <span>my tasks: {mp.done}/{mp.total} done · {mp.pending} pending · {mp.failed} failed</span>
                <span>{pct.toFixed(0)}% (campaign {c.campaign_progress?.pct?.toFixed(0) || 0}%)</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActivityFeed({ agentId }) {
  const q = useQuery({
    queryKey: ["activities", agentId],
    queryFn: () => api.get(`/agents/${agentId}/activities?days=30`).then(r => r.data),
  });
  return (
    <div className="border border-white/10">
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
        <Activity className="w-4 h-4 text-[#FF3B30]"/>
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">// activity log (30d)</div>
        <div className="ml-auto text-[11px] font-mono text-zinc-500">{q.data?.length || 0} entries</div>
      </div>
      <div className="max-h-[380px] overflow-y-auto">
        {q.data?.length === 0 && (
          <div className="p-8 text-center text-zinc-500 text-sm">
            No activity yet. Complete tasks or drift to fill this log.
          </div>
        )}
        {q.data?.map((a) => (
          <div key={a.id} className="px-4 py-3 border-b border-white/5 hover:bg-[#0e0e10]" data-testid={`activity-${a.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <span className={`px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border ${
                a.source === "campaign" ? "border-emerald-500/40 text-emerald-400" :
                a.source === "persona_drift" ? "border-[#007AFF]/40 text-[#007AFF]" :
                "border-white/10 text-zinc-400"
              }`}>{a.source}</span>
              <span className="font-mono text-[10px] text-[#FF3B30]">{a.subreddit || "—"}</span>
              <span className="text-zinc-700">·</span>
              <span className="font-mono text-[10px] text-zinc-500">{a.action_type}</span>
              <span className="ml-auto font-mono text-[9px] text-zinc-600">{a.created_at?.slice(0, 10)}</span>
            </div>
            <div className="text-[12px] text-zinc-300 line-clamp-2">{a.content_preview || <em className="text-zinc-600">(no content)</em>}</div>
            {a.post_url && <a href={a.post_url} target="_blank" rel="noreferrer" className="font-mono text-[10px] text-zinc-500 hover:text-white truncate block mt-1">{a.post_url}</a>}
          </div>
        ))}
      </div>
    </div>
  );
}

function AskAgent({ agentId, agentName }) {
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [pending, setPending] = useState(false);

  const send = async () => {
    if (!q.trim()) return;
    const question = q.trim(); setQ(""); setPending(true);
    setMessages(m => [...m, { role: "user", text: question }]);
    try {
      const r = await api.post(`/agents/${agentId}/ask`, { question, session_id: sessionId }, { timeout: 90000 });
      setSessionId(r.data.session_id);
      setMessages(m => [...m, { role: "assistant", text: r.data.answer }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ask failed");
    } finally { setPending(false); }
  };

  return (
    <div className="border border-white/10 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">// ask jarvis</div>
          <h3 className="font-display font-black text-xl tracking-tight">Query {agentName}'s history.</h3>
        </div>
        <button onClick={() => setExpanded(!expanded)} data-testid="ask-expand-btn" className="border border-white/10 hover:border-white/30 p-1.5">
          {expanded ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
        </button>
      </div>

      {expanded && messages.length > 0 && (
        <div className="mb-4 max-h-[300px] overflow-y-auto space-y-3 border border-white/5 p-3 bg-[#050505]" data-testid="ask-chat-log">
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "text-zinc-100" : "text-zinc-300"}>
              <div className={`text-[10px] font-mono uppercase tracking-widest mb-1 ${m.role === "user" ? "text-white" : "text-[#FF3B30]"}`}>
                {m.role === "user" ? "you" : "jarvis"}
              </div>
              <div className="text-[13px] leading-relaxed whitespace-pre-wrap">{m.text}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <input
          value={q}
          onChange={(e)=>setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder='e.g. "What has this agent posted about most in r/gardening?"'
          data-testid="ask-input"
          disabled={pending}
          className="flex-1 bg-[#050505] border border-white/10 px-3 py-2 text-[13px] disabled:opacity-50"
        />
        <button
          onClick={send}
          disabled={pending || !q.trim()}
          data-testid="ask-send-btn"
          className="bg-[#FF3B30] hover:bg-[#FF5A50] text-white px-4 py-2 flex items-center gap-2 disabled:opacity-40 text-[13px]"
        >
          {pending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
          ASK
        </button>
      </div>
      {!expanded && messages.length > 0 && (
        <div className="mt-3 text-[12px] text-zinc-300 line-clamp-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30] mr-2">latest:</span>
          {messages[messages.length - 1].text}
        </div>
      )}
    </div>
  );
}

function TelegramMessenger({ agent }) {
  const [text, setText] = useState("");
  const send = useMutation({
    mutationFn: () => api.post(`/agents/${agent.id}/telegram-message`, { text }),
    onSuccess: () => { toast.success(`Sent to ${agent.name}`); setText(""); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Send failed"),
  });
  const linked = !!agent.telegram_chat_id;
  return (
    <div className="border border-[var(--border)] bg-[var(--surface)] p-5" data-testid="telegram-messenger">
      <div className="flex items-center gap-2 mb-3">
        <Send className="w-4 h-4 text-emerald-400"/>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">// admin → agent</div>
          <div className="font-display font-black text-xl tracking-tight">Message on Telegram.</div>
        </div>
      </div>
      {!linked ? (
        <div className="text-[12px] text-amber-400 border border-amber-500/30 bg-amber-500/5 p-3" data-testid="telegram-not-linked">
          {agent.name} hasn't linked their Telegram yet. Share this app URL + Telegram tab with them to link.
        </div>
      ) : (
        <>
          <div className="text-[11px] font-mono text-emerald-400 mb-2">
            → linked as @{agent.telegram_username || "user"}
          </div>
          <textarea
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type your message. Supports basic HTML like <b>bold</b> and <i>italic</i>. Max 3900 chars."
            data-testid="telegram-message-input"
            className="w-full bg-[var(--input-bg)] border border-[var(--border)] focus:border-[var(--border-strong)] focus:outline-none px-3 py-2 text-[13px] mb-2"
          />
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-[10px] font-mono text-[var(--text-muted)]">{text.length}/3900</span>
          </div>
          <button
            onClick={() => send.mutate()}
            disabled={send.isPending || !text.trim()}
            data-testid="send-telegram-message-btn"
            className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white font-medium py-2 flex items-center justify-center gap-2 disabled:opacity-40"
          >
            {send.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
            SEND MESSAGE
          </button>
        </>
      )}
    </div>
  );
}

export default function AgentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = user?.role === "admin";
  const effectiveId = id || user?.id;

  const q = useQuery({
    queryKey: ["agent", effectiveId],
    queryFn: () => api.get(`/agents/${effectiveId}`).then(r => r.data),
  });

  const generateDrift = useMutation({
    mutationFn: () => api.post("/persona-drift/generate", { agent_id: effectiveId }, { timeout: 90000 }),
    onSuccess: (r) => { toast.success(`Scheduled ${r.data.created} drift task(s)`); qc.invalidateQueries({queryKey: ["agent-tasks"]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  if (q.isLoading || !q.data) return <Layout title="Agent"><div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/>Loading…</div></Layout>;
  const agent = q.data.agent;
  const stats = q.data.stats || {};

  return (
    <Layout
      subtitle={isAdmin ? <Link to="/admin/agents" className="hover:text-white flex items-center gap-1"><ArrowLeft className="w-3 h-3"/>Back to agents</Link> : "Your operator profile"}
      title={isAdmin ? `${agent.name}.` : "My Profile."}
      right={isAdmin && (
        <button
          onClick={() => generateDrift.mutate()}
          disabled={generateDrift.isPending}
          data-testid="gen-drift-btn"
          className="bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium px-4 py-2 flex items-center gap-2 disabled:opacity-50"
        >
          {generateDrift.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
          Generate weekly drift
        </button>
      )}
    >
      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border border-white/10 mb-6">
        {[
          ["Completed", stats.campaign_completed ?? 0],
          ["Active", stats.campaign_active ?? 0],
          ["Failed", stats.campaign_failed ?? 0],
          ["Log entries", stats.activities_30d ?? 0],
        ].map(([label, val], i) => (
          <div key={label} className={`px-4 py-3 ${i>0?"border-l border-white/10":""}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-0.5">{label}</div>
            <div className="font-display font-black text-2xl tracking-tighter">{val}</div>
          </div>
        ))}
      </div>

      <div className="grid xl:grid-cols-3 gap-6 min-w-0">
        <div className="xl:col-span-1 space-y-6 min-w-0">
          <ProfileCard agent={agent} isAdmin={isAdmin} onSaved={() => qc.invalidateQueries({queryKey:["agent", effectiveId]})}/>
          <PersonaEditor agent={agent} isAdmin={isAdmin} onSaved={() => qc.invalidateQueries({queryKey:["agent", effectiveId]})}/>
          {isAdmin && <TelegramMessenger agent={agent}/>}
          <AskAgent agentId={effectiveId} agentName={agent.name}/>
        </div>
        <div className="xl:col-span-2 space-y-6 min-w-0">
          <MindMap agentId={effectiveId} agentName={agent.name}/>
          <CampaignInvolvement agentId={effectiveId}/>
          <ActivityFeed agentId={effectiveId}/>
        </div>
      </div>
    </Layout>
  );
}
